import useStorage from '../../hooks/useStorage';
import { useCallback, useMemo } from 'react';
import { ChatInputModel } from '../../elements/chat-input';
import { LlmUIOption, PersonaOption } from '../../models/types';
import { KeyedEventHandler } from '../../services/EventHandler';

const PERSISTENT_NEW_PROMPTS_SCOPE = 'session-prompt';

const usePersistentInputState = () => {
    const { get, set, remove } = useStorage(PERSISTENT_NEW_PROMPTS_SCOPE, 'local', 'json');
    const events = useMemo(() => new KeyedEventHandler<string, ChatInputModel | undefined>(), []);
    const handleChangeInput = useCallback((sessionId: string, model: ChatInputModel) => {
        if (!model || !sessionId) return console.error('Invalid input model or session id');
        set(sessionId, model);
        events.invoke(sessionId, model);
    }, [set, events]);

    const handleRemoveInput = useCallback((sessionId: string) => {
        remove(sessionId);
        events.invoke(sessionId, undefined);
    }, [remove, events]);


    const handleGetInput = useCallback((sessionId: string, defaultModel?: ChatInputModel) => {
        const model = { ...(defaultModel ?? {}), ...(get(sessionId) as ChatInputModel) };
        return model;
    }, [get]);

    const handleMergeInput = useCallback((sessionId: string, inputModel: Partial<ChatInputModel>) => {
        const model = { ...(handleGetInput(sessionId) ?? {}), ...inputModel };
        return handleChangeInput(sessionId, model);
    }, [handleGetInput, handleChangeInput]);

    const onInputChange = useCallback((sessionId: string, handler: (model: ChatInputModel | undefined) => void) => {
        return events.on(sessionId, handler);
    }, [events]);

    const offInputChange = useCallback((sessionId: string, handler: (model: ChatInputModel | undefined) => void) => {
        return events.off(sessionId, handler);
    }, [events]);


    return {
        getInput: handleGetInput,
        setInput: handleChangeInput,
        mergeInput: handleMergeInput,
        removeInput: handleRemoveInput,
        onInputChange,
        offInputChange
    };
};

export default usePersistentInputState;


export const validateInputModelSelection = (model: ChatInputModel, llmOptions: LlmUIOption[], personaOptions: PersonaOption[]) => {
    const isValidLlmSelection = !model.llm || llmOptions.some(x => x.value === model.llm);
    const isValidPersonaSelection = !model.persona || personaOptions.some(x => x.key === model.persona);
    const isValidSelection = isValidLlmSelection && isValidPersonaSelection;
    return {
        isValidLlmSelection, isValidPersonaSelection, isValidSelection
    };
};